import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, CircularProgress } from "@mui/joy";
import type { PortalResponse } from "../../api/portal/Portal.typed";
import type { Training } from "../../api/training/Training.typed";
import { usePortal } from "../../contexts/api";
import DarkTypography from "../common/DarkTypography";
import { TheoryTestPage } from "./TheoryTestPage";
import { colorTokens, textTokens } from "../../styles/globalStyles";

export default function TheoryTestRoutePage() {
  const { trainingTemplateId: idParam } = useParams<{ trainingTemplateId: string }>();
  const routerNavigate = useNavigate();
  const goPortal = React.useCallback(() => {
    void routerNavigate("/portal");
  }, [routerNavigate]);

  const { getPortalCustomerInfo } = usePortal();

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [customer, setCustomer] = React.useState<PortalResponse | null>(null);
  const [training, setTraining] = React.useState<Training | null>(null);

  const templateId = idParam ? Number(idParam) : NaN;

  React.useEffect(() => {
    if (!idParam || Number.isNaN(templateId) || templateId <= 0) {
      setError("Formation invalide");
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const data = await getPortalCustomerInfo();
        if (cancelled) return;
        if (!data) {
          setError("Session invalide");
          return;
        }
        const t = data.Trainings?.find((tr) => tr.TrainingTemplateID === templateId);
        if (!t) {
          setError("Formation introuvable pour ce compte.");
          return;
        }
        setCustomer(data);
        setTraining(t);
      } catch {
        if (!cancelled) {
          setError("Impossible de charger vos données.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [idParam, templateId, getPortalCustomerInfo]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: colorTokens.bg.app,
        }}
      >
        <CircularProgress sx={{ color: textTokens.color.primary }} />
      </Box>
    );
  }

  if (error || !customer || !training) {
    return (
      <Box
        sx={{
          p: 3,
          minHeight: "100vh",
          bgcolor: colorTokens.bg.app,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          maxWidth: "95vw",
          mx: "auto",
        }}
      >
        <DarkTypography level="title-md" colorVariant="title" sx={{ textAlign: "center" }}>
          {error ?? "Erreur"}
        </DarkTypography>
        <Button variant="solid" onClick={goPortal} sx={{ minHeight: 44 }}>
          Retour au portail
        </Button>
      </Box>
    );
  }

  return (
    <TheoryTestPage
      trainingTemplateId={templateId}
      customerId={customer.Id}
      firstName={customer.FirstName}
      agency={training.Agency}
      qualification={training.Qualification}
      theoryTests={training.TheoryTests ?? []}
      onClose={goPortal}
      onCancel={goPortal}
    />
  );
}
